import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as yup from "yup";
import { Dialog } from "component/shared/dialog";
import { LabeledField } from "component/shared/profile-refresh/labeled-field";
import { createOffice } from "redux/slice/offices";
import { getPayload } from "redux/store";
import { validate } from "shared/yup";
import { styles } from "../style";
import * as Router from "react-router-dom";
import { urls } from "routes/urls";
import * as env from "shared/env";

interface Props
	extends OwnProps,
		ReturnType<typeof mapDispatchToProps>,
		Router.RouteComponentProps,
		WithStyles<typeof styles> {}

interface OwnProps {
	onClose: () => void;
	open: boolean;
}

interface State {
	name?: string;	
	email?: string;
	submitted: boolean;
}

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	createOffice,
}, dispatch);

const initialState: State = {
	name: "",
	email: "",
	submitted: false,
};

class Component extends React.Component<Props, State> {
	public constructor(props: Props) {
		super(props);
		this.state = initialState;
	}

	public componentDidUpdate(prevProps: Props) {
		if (!prevProps.open && this.props.open) {
			this.setState(initialState);
		}
	}

	private submit = async () => {
		const { onClose, history } = this.props;
		const {
			name,
			email,
		} = this.state;
		this.setState({ submitted: true });
		const errors = validate(this.validationSchema, this.state);
		if (!errors) {
			// The schema requires both fields, so reaching here means they are non-empty;
			// the fallbacks only satisfy the string-typed payload.
			const officeResource = getPayload(await this.props.createOffice( { office: 
				{
					name: name || "",
					email: email || "",
				}
			}));
			if (officeResource && officeResource.id) {
				history.push(urls.settingsOfficeById(officeResource.id));
				onClose();
			}
		}
	};

	// Copy matches the Figma error state. Email uses the same message for "missing" and
	// "malformed" because the design shows one string for the field.
	private validationSchema = {
		name: yup.string().required("An office name is required."),
		email: yup
			.string()
			.email("A valid email is required.")
			.required("A valid email is required."),
	};

	render() {
		const { classes = {}, onClose, open } = this.props;
		const {
			name,
			email,
			submitted,
		} = this.state;
		const errors = validate(this.validationSchema, this.state);
		return (
			<Dialog
				classes={{ paper: classes.dialogPaper }}
				disableBackdropClick
				onClose={() => onClose()}
				scroll="paper"
				open={open}
			>
				<Mui.DialogTitle>
					<Mui.Typography component="h2" variant="h4">
						Add Office
					</Mui.Typography>
					<Mui.IconButton
						className={classes.closeButton}
						onClick={() => onClose()}
					>
						<Icons.X />
					</Mui.IconButton>
					<Mui.Divider className={classes.titleDivider} />
				</Mui.DialogTitle>
				<Mui.DialogContent>
					<Mui.Typography className={classes.sectionHeader}>General</Mui.Typography>
					<div className={classes.fields}>
						<LabeledField
							label="Office Name"
							required
							error={submitted && !!errors && !!errors.name}
							helperText={errors && errors.name}
							value={name || ""}
							onChange={(value) => this.setState({ name: value })}
						/>
						<LabeledField
							label="Email"
							required
							error={submitted && !!errors && !!errors.email}
							helperText={errors && errors.email}
							value={email || ""}
							onChange={(value) => this.setState({ email: value })}
						/>
					</div>
				</Mui.DialogContent>
				<Mui.DialogActions className={classes.dialogActions}>
					<Mui.Button
						className={classes.button}
						variant="outlined"
						color={"primary"}
						onClick={() => onClose()}
					>
						Cancel
					</Mui.Button>
					<Mui.Button
						className={classes.button}
						variant="contained"
						color={"primary"}
						onClick={() => this.submit()}
					>
						Add Office
					</Mui.Button>
				</Mui.DialogActions>
			</Dialog>
		);
	}
}

export const AddOfficeDialog = Router.withRouter(withStyles(connect(null, mapDispatchToProps)(Component), styles)
);